import { useCallback, useEffect, useState } from "react";

import { getMenuOptions } from "services/menu/rest";
import { MenuOptionDto } from "services/menu/restModels";

export interface UseMenuOptionsResult {
    menuOptions: MenuOptionDto[];
    loading: boolean;
    error: boolean;
    refetch: () => void;
}

/*
* Меню для селекта — справочник: его читают выбор меню на промокодах, диалог «Копировать
* в меню…» и заголовок редактора меню. Кешируем на уровне модуля (как useMenus): меню
* создают редко, а запрос иначе повторялся бы на каждом открытии диалога. Общий промис не
* даёт параллельным монтированиям плодить запросы.
*
* Отдельно от useMenus: тот несёт счётчики категорий и позиций, ради которых бэк делает две
* агрегации по всем позициям филиала, и нужны они одному экрану — списку меню.
* ⚠️ Кеша, соответственно, два. Название меню правится в настройках меню — оттуда
* сбрасываются оба (см. useMenuSettingsState).
* */
let cache: MenuOptionDto[] | null = null;
let inFlight: Promise<MenuOptionDto[] | null> | null = null;

const loadMenuOptions = (): Promise<MenuOptionDto[] | null> => {
    if (cache) return Promise.resolve(cache);

    if (!inFlight) {
        inFlight = getMenuOptions()
            .then((result) => {
                if (result) cache = result;
                return result;
            })
            .finally(() => {
                inFlight = null;
            });
    }

    return inFlight;
};

export const useMenuOptions = (): UseMenuOptionsResult => {
    const [menuOptions, setMenuOptions] = useState<MenuOptionDto[]>(cache || []);
    const [loading, setLoading] = useState<boolean>(!cache);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    const refetch = useCallback(() => {
        cache = null;
        setReloadKey((key) => key + 1);
    }, []);

    useEffect(() => {
        // ignore-флаг: ответ после размонтирования не трогает стейт.
        let ignore = false;

        setLoading(true);
        setError(false);

        loadMenuOptions()
            .then((result) => {
                if (ignore) return;
                if (result) {
                    setMenuOptions(result);
                } else {
                    setError(true);
                    setMenuOptions([]);
                }
            })
            .catch(() => {
                if (ignore) return;
                setError(true);
                setMenuOptions([]);
            })
            .finally(() => {
                if (ignore) return;
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [reloadKey]);

    return { menuOptions, loading, error, refetch };
};
